import { useMemo } from 'react';
import { FileTypeEnum, SimulationDocumentDto } from 'api/documents';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';
import { useDocuments } from '../contexts';

/**
 * Ordre d'affichage des documents
 */
export const DOCUMENT_ORDER: FileTypeEnum[] = [
  FileTypeEnum.BulletinAdhesion,
  FileTypeEnum.BulletinAdhesionSigne,
  FileTypeEnum.MandatSubstitutionAssurance,
  FileTypeEnum.MandatSubstitutionAssuranceSigne,
  FileTypeEnum.CertificatAdhesion,
  FileTypeEnum.QuittanceGenerer,
  FileTypeEnum.SimulationComplete,
  FileTypeEnum.AdviceSheetPdfGenerer,
  FileTypeEnum.Ipid,
  FileTypeEnum.Notice,
  FileTypeEnum.ConditionsSpeciales
];

/**
 * Trie les documents selon l'ordre défini
 */
export const sortDocuments = (
  docs: SimulationDocumentDto[]
): SimulationDocumentDto[] => {
  return [...docs].sort((a, b) => {
    const indexA = DOCUMENT_ORDER.indexOf(a.fileType);
    const indexB = DOCUMENT_ORDER.indexOf(b.fileType);

    if (indexA === -1 && indexB === -1) return 0;
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;

    return indexA - indexB;
  });
};

/**
 * Hook retournant les documents triés et traduits
 *
 * @example
 * ```tsx
 * const sortedDocuments = useSortedDocuments();
 * ```
 */
export const useSortedDocuments = (): SimulationDocumentDto[] => {
  const documents = useDocuments();
  const { t } = useDynamicTranslation([NsKey.Contractualization, NsKey.Document]);

  return useMemo(() => {
    // Traduire les labels
    const translatedDocs = documents.map(doc => {
      const newLabel = doc.label;
      if (newLabel) {
        return {
          ...doc,
          label: t(newLabel)
        };
      }
      return doc;
    });

    // Trier selon l'ordre défini
    return sortDocuments(translatedDocs);
  }, [documents, t]);
};
